import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import { useSectionContent } from '@/hooks/useSectionContent';
import { useThemePageTracking } from '@/hooks/useThemePageTracking';
import { useAuth } from '@/hooks/useAuth';
import { useFavorites } from '@/hooks/useFavorites';
import FavoriteBadge from '@/components/feature/FavoriteBadge';
import { normalizeCurrency } from '@/lib/currency';
import { useDisplayCurrency } from '@/hooks/useDisplayCurrency';

interface ServiceItem {
  id: number;
  titre: string;
  description: string;
  detailssup: string | null;
  product_image: string;
  prix: number;
  prix_promo: number | null;
  tags: string | null;
  pays: string | null;
  ville: string | null;
  created_at: string;
  categorie_id: number | null;
}

interface ServiceCategory {
  id: number;
  nom: string;
  icon: string | null;
  image_couverture: string | null;
  parent_id: number | null;
}

export default function ServicesPublic() {
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [activeCategory, setActiveCategory] = useState<number | null>(null);
  const [activeSubCategory, setActiveSubCategory] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { tenant, isTenant } = useTenant();
  const { before, highlight, subtitle } = useSectionContent('services');
  // Suivi de visite de la page services publique (vues, IP, géo, provenance).
  useThemePageTracking('services', tenant?.id ?? null, isTenant && !!tenant);
  const { isFav, toggle: toggleFavorite } = useFavorites();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Bascule un favori depuis la liste publique (badge « déjà en favori »).
  const toggleFavItem = (id: number, label: string, metadata: Record<string, unknown>) => {
    if (!user) { navigate('/login-client'); return; }
    void toggleFavorite({ userId: user.id, entityType: 'service', entityId: id, label, metadata });
  };

  const monaie = normalizeCurrency(tenant?.monaie || 'MAD');
  // Prix des services affichés dans la devise choisie par le visiteur
  // (conversion au taux du jour), la monnaie du marchand restant la référence.
  const { display, original } = useDisplayCurrency(monaie);

  const getServiceImages = (productImage: string): string[] => {
    if (!productImage) return [];
    return productImage.split('|').filter(Boolean).filter((url) => {
      const lower = url.toLowerCase();
      return !(lower.endsWith('.mp4') || lower.endsWith('.webm') || lower.endsWith('.mov'));
    });
  };

  const getCoverImage = (productImage: string): string => {
    return getServiceImages(productImage)[0] || '';
  };

  const fetchCategories = useCallback(async () => {
    if (!isTenant || !tenant) {
      setCategories([]);
      return;
    }
    try {
      const { data } = await supabase
        .from('categorieservices')
        .select('id, nom, image_couverture, parent_id, sort_order')
        .eq('idcommerce', tenant.id)
        .order('sort_order', { ascending: true })
        .order('nom', { ascending: true });
      if (data) {
        setCategories((data as { id: number; nom: string; image_couverture: string | null; parent_id: number | null }[]).map((c) => ({
          id: c.id,
          nom: c.nom,
          icon: null,
          image_couverture: c.image_couverture,
          parent_id: c.parent_id,
        })));
      }
    } catch {
      // silent
    }
  }, [isTenant, tenant]);

  useEffect(() => {
    setLoading(true);
    fetchCategories();
    let query = supabase
      .from('nospartenairesservices')
      .select('*');

    if (isTenant && tenant) {
      query = query.eq('idcommerce', tenant.id);
    }

    query.order('created_at', { ascending: false })
      .then(({ data, error: fetchError }) => {
        if (fetchError) setError(fetchError.message);
        else setServices((data as ServiceItem[]) || []);
      })
      .catch(() => setError('Erreur de chargement'))
      .finally(() => setLoading(false));
  }, [fetchCategories, isTenant, tenant]);

  const rootCategories = categories.filter((c) => !c.parent_id);
  const subCategories = activeCategory ? categories.filter((c) => c.parent_id === activeCategory) : [];

  const visibleServices = (() => {
    let list = services;
    if (activeSubCategory) list = list.filter((s) => s.categorie_id === activeSubCategory);
    else if (activeCategory) {
      const childIds = categories.filter((c) => c.parent_id === activeCategory).map((c) => c.id);
      const ids = new Set<number>([activeCategory, ...childIds]);
      list = list.filter((s) => s.categorie_id != null && ids.has(s.categorie_id));
    }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((s) =>
        [s.titre, s.description, s.tags].filter(Boolean).join(' ').toLowerCase().includes(q),
      );
    }
    return list;
  })();

  const renderCategoryIcon = (icon: string | null, image: string | null, active: boolean) => {
    if (icon) return <i className={`${icon} text-sm`}></i>;
    if (image) {
      return (
        <span className={`w-5 h-5 rounded-full overflow-hidden flex-shrink-0 border ${active ? 'border-white/40' : 'border-background-200/70'}`}>
          <img src={image} alt="" className="w-full h-full object-cover" />
        </span>
      );
    }
    return null;
  };

  // Nombre de services par catégorie (une catégorie racine compte ses sous-catégories).
  const countForCategory = (catId: number): number => {
    const childIds = categories.filter((c) => c.parent_id === catId).map((c) => c.id);
    const ids = new Set<number>([catId, ...childIds]);
    return services.filter((s) => s.categorie_id != null && ids.has(s.categorie_id)).length;
  };
  const countForSubCategory = (catId: number): number =>
    services.filter((s) => s.categorie_id === catId).length;

  // Chaîne de catégorie complète d'un contenu (Catégorie › Sous-catégorie › Sous-sous-catégorie).
  const categoryById = new Map(categories.map((c) => [c.id, c]));
  const categoryChainFor = (id: number | null): { id: number; nom: string }[] => {
    const out: { id: number; nom: string }[] = [];
    let cur: number | null = id;
    let guard = 0;
    while (cur && guard < 6) {
      guard += 1;
      const c = categoryById.get(cur);
      if (!c) break;
      out.unshift({ id: c.id, nom: c.nom });
      cur = c.parent_id ?? null;
    }
    return out;
  };

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background-100/50 to-transparent"></div>
        <div className="relative w-full px-4 md:px-6 max-w-7xl mx-auto text-center">
          <h1 className="text-3xl md:text-5xl font-bold font-heading text-foreground-950 mb-4">
            {before}
            {before && highlight ? ' ' : ''}
            {highlight && <span className="text-primary-500">{highlight}</span>}
          </h1>
          <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
            {subtitle}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-8 md:py-12">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          {!loading && !error && services.length > 0 && (
            <div className="mb-5">
              <div className="relative w-full sm:max-w-md">
                <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher un service…"
                  className="w-full pl-10 pr-10 py-2.5 rounded-full border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-700 transition-colors cursor-pointer"
                    aria-label="Effacer la recherche"
                  >
                    <i className="ri-close-circle-line"></i>
                  </button>
                )}
              </div>
            </div>
          )}
          {!loading && !error && categories.length > 0 && (
            <div className="mb-6 space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => { setActiveCategory(null); setActiveSubCategory(null); }}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                    activeCategory === null
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 border border-background-200/70 text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  <i className="ri-apps-2-line"></i>
                  Tous
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${activeCategory === null ? 'bg-white/25 text-background-50' : 'bg-background-200/70 text-foreground-600'}`}>{services.length}</span>
                </button>
                {rootCategories.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => { setActiveCategory(cat.id); setActiveSubCategory(null); }}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                      activeCategory === cat.id
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 border border-background-200/70 text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    {renderCategoryIcon(cat.icon, cat.image_couverture, activeCategory === cat.id)}
                    {cat.nom}
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${activeCategory === cat.id ? 'bg-white/25 text-background-50' : 'bg-background-200/70 text-foreground-600'}`}>{countForCategory(cat.id)}</span>
                  </button>
                ))}
              </div>
              {subCategories.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap pl-1">
                  <span className="text-xs text-foreground-400 flex items-center gap-1">
                    <i className="ri-corner-down-right-line"></i>
                    Sous-catégories :
                  </span>
                  <button
                    type="button"
                    onClick={() => setActiveSubCategory(null)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                      activeSubCategory === null
                        ? 'bg-accent-500 text-background-50'
                        : 'bg-accent-50 text-accent-700 hover:bg-accent-100'
                    }`}
                  >
                    Tout afficher
                  </button>
                  {subCategories.map((sub) => (
                    <button
                      key={sub.id}
                      type="button"
                      onClick={() => setActiveSubCategory(sub.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                        activeSubCategory === sub.id
                          ? 'bg-accent-500 text-background-50'
                          : 'bg-accent-50 text-accent-700 hover:bg-accent-100'
                      }`}
                    >
                      {renderCategoryIcon(sub.icon, sub.image_couverture, activeSubCategory === sub.id)}
                      {sub.nom}
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${activeSubCategory === sub.id ? 'bg-white/25 text-background-50' : 'bg-white/70 text-accent-800'}`}>{countForSubCategory(sub.id)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {!loading && !error && (search.trim() || activeCategory !== null || activeSubCategory !== null) && (
            <div className="mb-5 flex items-center gap-2 flex-wrap px-3.5 py-2.5 rounded-lg bg-primary-50 border border-primary-200/60">
              <i className="ri-filter-3-line text-primary-600"></i>
              <span className="text-sm font-semibold text-foreground-800">
                {visibleServices.length} résultat{visibleServices.length > 1 ? 's' : ''}
              </span>
              {search.trim() && <span className="text-xs text-foreground-500">pour « {search.trim()} »</span>}
              {activeSubCategory !== null && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-900 font-medium">
                  {categories.find((c) => c.id === activeSubCategory)?.nom}
                </span>
              )}
              {activeSubCategory === null && activeCategory !== null && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 font-medium">
                  {categories.find((c) => c.id === activeCategory)?.nom}
                </span>
              )}
              <button
                type="button"
                onClick={() => { setSearch(''); setActiveCategory(null); setActiveSubCategory(null); }}
                className="ml-auto text-xs font-medium text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-close-line mr-0.5"></i>Réinitialiser
              </button>
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20">
              <i className="ri-error-warning-line text-4xl text-red-400 mb-3"></i>
              <p className="text-foreground-500">{error}</p>
            </div>
          ) : visibleServices.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
                <i className="ri-service-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">{search.trim() ? 'Aucun service ne correspond à votre recherche' : 'Aucun service pour le moment'}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {visibleServices.map((service) => {
                const coverUrl = getCoverImage(service.product_image);
                const chain = categoryChainFor(service.categorie_id);
                return (
                  <div key={service.id} className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-background-300/60 transition-all duration-200">
                    <div className="h-48 bg-background-100 overflow-hidden relative">
                      {coverUrl ? (
                        <img
                          src={coverUrl}
                          alt={service.titre}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <i className="ri-service-line text-3xl text-foreground-300"></i>
                        </div>
                      )}
                      <FavoriteBadge
                        active={isFav('service', service.id)}
                        onToggle={() => toggleFavItem(service.id, service.titre, { image: coverUrl || null, ville: service.ville || null, price: service.prix_promo ?? service.prix, currency: monaie })}
                        className="absolute top-2 right-2 z-10"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="text-base font-semibold text-foreground-900">{service.titre}</h3>
                        <div className="text-right flex-shrink-0">
                          {service.prix_promo ? (
                            <>
                              <span className="text-base font-bold text-primary-600 block">{display(Number(service.prix_promo), monaie)}</span>
                              <span className="text-xs text-foreground-400 line-through">{display(Number(service.prix), monaie)}</span>
                            </>
                          ) : (
                            <span className="text-base font-bold text-primary-600">{display(Number(service.prix), monaie)}</span>
                          )}
                        </div>
                      </div>
                      {chain.length > 0 && (
                        <div className="flex items-center gap-1 flex-wrap mb-2">
                          {chain.map((c, idx) => (
                            <span key={c.id} className="flex items-center gap-1">
                              {idx > 0 && <i className="ri-arrow-right-s-line text-foreground-300 text-[10px]"></i>}
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary-50 text-secondary-700 whitespace-nowrap">{c.nom}</span>
                            </span>
                          ))}
                        </div>
                      )}
                      <p className="text-sm text-foreground-500 mb-3 line-clamp-2">{service.description}</p>
                      {service.tags && (
                        <div className="flex items-center gap-1.5 flex-wrap mb-3">
                          {service.tags.split(',').map((tag) => (
                            <span key={tag} className="px-2 py-0.5 bg-secondary-50 text-secondary-700 rounded-full text-xs">{tag.trim()}</span>
                          ))}
                        </div>
                      )}
                      {original(Number(service.prix_promo ?? service.prix), monaie) && (
                        <span className="block -mt-2 mb-2 text-[11px] text-foreground-400">
                          {original(Number(service.prix_promo ?? service.prix), monaie)}
                        </span>
                      )}
                      {service.detailssup && (
                        <p className="text-xs text-foreground-400 mt-2 line-clamp-3">{service.detailssup}</p>
                      )}
                      <div className="mt-3 pt-3 border-t border-background-200/70 flex items-center justify-between gap-3">
                        <span className="text-xs text-foreground-400 truncate">
                          <i className="ri-map-pin-line mr-1"></i>
                          {service.ville && service.pays ? `${service.ville}, ${service.pays}` : service.ville || service.pays || 'Non spécifié'}
                        </span>
                        <Link
                          to={`/service/${service.id}`}
                          className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors no-underline whitespace-nowrap"
                        >
                          <i className="ri-eye-line"></i>
                          Voir
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}